import CONSTANTS from '../constants/constants';

import { CareCoordinatorReferral, Referral } from 'types';

import * as api from 'api/referrals';
import { fetchCareCoordinatorReferrals } from 'api/care_coordinator_referrals';

import { handleSamaritanApiError } from './error';

type ReferralParams =
    | { homelessID: number; orgID?: never; userID?: never }
    | { orgID: number; userID?: number; homelessID?: never }
    | { userID: number; orgID?: number; homelessID?: never };

export function acceptReferral(referralID: number) {
    return {
        type: CONSTANTS.ACCEPT_REFERRAL,
        payload: referralID
    };
}

function fetchIncomingReferrals() {
    return {
        type: CONSTANTS.FETCH_INCOMING_REFERRALS
    };
}

function fetchMemberReferrals() {
    return {
        type: CONSTANTS.FETCH_MEMBER_REFERRALS
    };
}

function fetchOutgoingReferrals() {
    return {
        type: CONSTANTS.FETCH_OUTGOING_REFERRALS
    };
}

function fetchUniqueMemberInfo() {
    return {
        type: CONSTANTS.FETCH_UNIQUE_MEMBER_INFO
    };
}

export function rejectReferral(referralID: number) {
    return {
        type: CONSTANTS.REJECT_REFERRAL,
        payload: referralID
    };
}

export function resetMemberReferrals() {
    return {
        type: CONSTANTS.RESET_MEMBER_REFERRALS
    };
}

export function resetUserReferrals() {
    return {
        type: CONSTANTS.RESET_USER_REFERRALS
    };
}

function setIncomingReferrals(list: Referral[]) {
    return {
        type: CONSTANTS.SET_INCOMING_REFERRALS,
        payload: list
    };
}

function setOutgoingReferrals(list: Referral[]) {
    return {
        type: CONSTANTS.SET_OUTGOING_REFERRALS,
        payload: list
    };
}

function setMemberReferrals(list: Referral[]) {
    return {
        type: CONSTANTS.SET_MEMBER_REFERRALS,
        payload: list
    };
}

function setUniqueMemberInfo(info: any) {
    return {
        type: CONSTANTS.SET_UNIQUE_MEMBER_INFO,
        payload: info
    };
}

export function updateMemberReferrals(referral: Referral) {
    return {
        type: CONSTANTS.UPDATE_MEMBER_REFERRALS,
        payload: referral
    };
}

export const fetchMemberReferralsRequest = (homelessID: number) => {
    return async (dispatch: Function) => {
        try {
            dispatch(fetchMemberReferrals());

            const responses = await api.fetchReferrals({ homelessID });

            const homelessResponse = responses.find(
                (res) => res.queryType === 'homelessID'
            );

            if (homelessResponse?.success && homelessResponse.response) {
                const resJson = await homelessResponse.response.json();

                dispatch(setMemberReferrals(resJson.ctp_caseload_referrals));
            } else {
                console.error(
                    'Failed to fetch member referrals:',
                    homelessResponse?.response?.statusText || 'Unknown error'
                );
            }
        } catch (e: any) {
            handleSamaritanApiError(e, dispatch);
        }
    };
};

export const fetchUserReferralsRequest = (userID: number, orgID?: number) => {
    const allHomelessIDs: number[] = [];
    let allFilteredReferrals: Referral[] = [];

    return async (dispatch: Function) => {
        try {
            if (!orgID) {
                dispatch(fetchOutgoingReferrals());
            } else {
                dispatch(fetchIncomingReferrals());
            }

            const params: ReferralParams = orgID
                ? { orgID, userID }
                : { userID };

            const responses = await api.fetchReferrals(params);
            const responseQueryTypes = responses.map((res) => res.queryType);

            for (const { success, response, queryType } of responses) {
                if (success && response) {
                    const resJson = await response.json();

                    if (['userID', 'orgID'].includes(queryType)) {
                        const homelessIDs = Array.from(
                            new Set(
                                resJson.ctp_caseload_referrals.map(
                                    (referral: Referral) => referral.homeless_id
                                )
                            )
                        ) as number[];

                        homelessIDs.forEach((id) => {
                            if (!allHomelessIDs.includes(id)) {
                                allHomelessIDs.push(id);
                            }
                        });

                        let filteredReferrals = resJson.ctp_caseload_referrals;

                        if (responseQueryTypes.length === 2) {
                            filteredReferrals = filteredReferrals.filter(
                                (referral: Referral) => {
                                    return (
                                        referral.organization?.referee_ids.includes(
                                            userID
                                        ) || referral.referee_id === userID
                                    );
                                }
                            );
                        }

                        allFilteredReferrals =
                            allFilteredReferrals.concat(filteredReferrals);
                    } else {
                        console.warn(
                            `Unexpected query type "${queryType}" in API response.`
                        );
                    }
                } else {
                    console.error(
                        `Failed to fetch referrals for ${queryType}.`
                    );
                }
            }

            const dispatchReferrals =
                responseQueryTypes.length === 1
                    ? setOutgoingReferrals
                    : setIncomingReferrals;

            const sortedReferrals = allFilteredReferrals.sort(
                (a, b) =>
                    new Date(b.created_at).getTime() -
                    new Date(a.created_at).getTime()
            );

            dispatch(dispatchReferrals(sortedReferrals));

            dispatch(fetchUniqueMemberInfoRequest(allHomelessIDs));
        } catch (e: any) {
            handleSamaritanApiError(e, dispatch);
        }
    };
};

export const fetchUniqueMemberInfoRequest = (homelessIDs: number[]) => {
    return async (dispatch: Function) => {
        try {
            dispatch(fetchUniqueMemberInfo());

            const response = await api.fetchUniqueMemberInfo(homelessIDs);

            if (response && response.ok) {
                const resJson = await response.json();

                dispatch(setUniqueMemberInfo(resJson.samaritan_members));
            } else {
                console.error(
                    'Failed to fetch unique member info:',
                    response?.statusText || 'Unknown error'
                );
            }
        } catch (e: any) {
            handleSamaritanApiError(e, dispatch);
        }
    };
};

function fetchIncomingCareCoordinatorReferralsAction() {
    return {
        type: CONSTANTS.FETCH_INCOMING_CARE_COORDINATOR_REFERRALS
    };
}

function setIncomingCareCoordinatorReferrals(referrals: CareCoordinatorReferral[]) {
    return {
        type: CONSTANTS.SET_INCOMING_CARE_COORDINATOR_REFERRALS,
        payload: referrals
    };
}

function fetchOutgoingCareCoordinatorReferralsAction() {
    return {
        type: CONSTANTS.FETCH_OUTGOING_CARE_COORDINATOR_REFERRALS
    };
}

function setOutgoingCareCoordinatorReferrals(referrals: CareCoordinatorReferral[]) {
    return {
        type: CONSTANTS.SET_OUTGOING_CARE_COORDINATOR_REFERRALS,
        payload: referrals
    };
}

export const fetchIncomingCareCoordinatorReferralsRequest = (userID: number) => {
    return async (dispatch: Function) => {
        try {
            dispatch(fetchIncomingCareCoordinatorReferralsAction());

            const response = await fetchCareCoordinatorReferrals({
                refereeId: userID
            });

            if (response && response.ok) {
                const responseJson = await response.json();
                dispatch(setIncomingCareCoordinatorReferrals(responseJson.care_coordinator_referrals));
            } else {
                console.error('Failed to fetch incoming care coordinator referrals:', response?.statusText || 'Unknown error');
            }
        } catch (e: any) {
            handleSamaritanApiError(e, dispatch);
        }
    };
};

export const fetchOutgoingCareCoordinatorReferralsRequest = (userID: number) => {
    return async (dispatch: Function) => {
        try {
            dispatch(fetchOutgoingCareCoordinatorReferralsAction());

            const response = await fetchCareCoordinatorReferrals({
                referrerId: userID
            });

            if (response && response.ok) {
                const responseJson = await response.json();
                dispatch(setOutgoingCareCoordinatorReferrals(responseJson.care_coordinator_referrals));
            } else {
                console.error('Failed to fetch outgoing care coordinator referrals:', response?.statusText || 'Unknown error');
            }
        } catch (e: any) {
            handleSamaritanApiError(e, dispatch);
        }
    };
};

function fetchMemberCareCoordinatorReferralsAction() {
    return {
        type: CONSTANTS.FETCH_MEMBER_CARE_COORDINATOR_REFERRALS
    };
}

function setMemberCareCoordinatorReferrals(referrals: CareCoordinatorReferral[]) {
    return {
        type: CONSTANTS.SET_MEMBER_CARE_COORDINATOR_REFERRALS,
        payload: referrals
    };
}

export const fetchMemberCareCoordinatorReferralsRequest = (homelessID: number) => {
    return async (dispatch: Function) => {
        try {
            dispatch(fetchMemberCareCoordinatorReferralsAction());

            const response = await fetchCareCoordinatorReferrals({
                homelessId: homelessID
            });

            if (response && response.ok) {
                const responseJson = await response.json();
                dispatch(setMemberCareCoordinatorReferrals(responseJson.care_coordinator_referrals));
            } else {
                console.error('Failed to fetch member care coordinator referrals:', response?.statusText || 'Unknown error');
            }
        } catch (e: any) {
            handleSamaritanApiError(e, dispatch);
        }
    };
};